import { StyleSheet, Text, View, ActivityIndicator } from 'react-native';

import { UpcomingEventCard } from './UpcomingEventCard';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getBodyFont } from '@/constants/typography';
import { useEventAvatarResolver } from '@/hooks/use-event-avatar-resolver';
import { getLightBackground, getDarkBackground } from '@/lib/calendar/EventDisplayUtils';
import { EventFadeCalculator } from '@/lib/calendar/EventFadeCalculator';
import type { CalendarEvent } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

export type UpcomingCardProps = {
  events: CalendarEvent[];
  todaysAllDayEvents: CalendarEvent[];
  todaysCompletedEvents: CalendarEvent[];
  isLoading: boolean;
  onEventPress?: (event: CalendarEvent) => void;
  familyMembers: FamilyMember[];
  calendarMappings: CalendarMemberMapping[];
  deviceCalendars: DeviceCalendar[];
};

type EventDateParts =
  | { isToday: true }
  | { isToday: false; dayAbbrev: string; dateNum: string; monthAbbrev: string };

/**
 * Parse event date into structured parts for stacked display.
 * Returns { isToday: true } for today, or day/date/month parts for other dates.
 */
function parseEventDateParts(dateStr: string): EventDateParts {
  const eventDate = new Date(`${dateStr}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (eventDate.getTime() === today.getTime()) {
    return { isToday: true };
  }

  const dayAbbrev = eventDate.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const dateNum = String(eventDate.getDate());
  const monthAbbrev = eventDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();

  return { isToday: false, dayAbbrev, dateNum, monthAbbrev };
}

/**
 * UpcomingCard - Displays today's events and next 3 upcoming events in grouped sections.
 * Groups: "All day" (today's all-day), "Today" (today's completed, faded), "Coming up" (future)
 */
export function UpcomingCard({
  events,
  todaysAllDayEvents,
  todaysCompletedEvents,
  isLoading,
  onEventPress,
  familyMembers,
  calendarMappings,
  deviceCalendars,
}: UpcomingCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const { resolveEventAvatar } = useEventAvatarResolver({
    familyMembers,
    calendarMappings,
    deviceCalendars,
    defaultColor: palette.primary,
  });

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={palette.primary} />
      </View>
    );
  }

  const hasAnyEvents = todaysAllDayEvents.length > 0 || todaysCompletedEvents.length > 0 || events.length > 0;

  if (!hasAnyEvents) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          No upcoming events
        </Text>
      </View>
    );
  }

  const renderDateColumn = (dateParts: EventDateParts) => {
    if (dateParts.isToday) {
      return (
        <Text style={[styles.todayText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          Today
        </Text>
      );
    }

    return (
      <>
        <Text style={[styles.dayAbbrev, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {dateParts.dayAbbrev}
        </Text>
        <Text style={[styles.dateNum, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {dateParts.dateNum}
        </Text>
        <Text style={[styles.monthAbbrev, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {dateParts.monthAbbrev}
        </Text>
      </>
    );
  };

  const renderEventRow = (event: CalendarEvent, index: number, forcePast?: boolean) => {
    const avatar = resolveEventAvatar(event);
    const tintedBackground = avatar.color
      ? (colorScheme === 'dark' ? getDarkBackground(avatar.color) : getLightBackground(avatar.color))
      : undefined;
    const isPast = forcePast ?? EventFadeCalculator.isEventPast(event.startDate, event.startTime);
    const dateParts = parseEventDateParts(event.startDate);

    return (
      <View key={`${event.deviceCalendarId}-${event.id}-${index}`} style={styles.eventRow}>
        <View style={styles.dateColumn}>
          {renderDateColumn(dateParts)}
        </View>
        <View style={styles.cardColumn}>
          <UpcomingEventCard
            event={event}
            onPress={onEventPress ? () => onEventPress(event) : undefined}
            avatarImageUri={avatar.imageUri}
            avatarColor={avatar.color}
            avatarName={avatar.name}
            tintedBackground={tintedBackground}
            isPast={isPast}
          />
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {todaysAllDayEvents.length > 0 &&
        todaysAllDayEvents.map((event, i) => renderEventRow(event, i))}

      {todaysCompletedEvents.length > 0 &&
        todaysCompletedEvents.map((event, i) => renderEventRow(event, i, true))}

      {events.length > 0 && events.map((event, i) => renderEventRow(event, i))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: Spacing.sm,
  },
  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateColumn: {
    width: 45,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  todayText: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'left',
  },
  dayAbbrev: {
    fontSize: 9,
    fontWeight: '500',
    textAlign: 'left',
    letterSpacing: 0.5,
  },
  dateNum: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'left',
    lineHeight: 18,
  },
  monthAbbrev: {
    fontSize: 9,
    fontWeight: '500',
    textAlign: 'left',
    letterSpacing: 0.5,
  },
  cardColumn: {
    flex: 1,
  },
  loadingContainer: {
    paddingVertical: Spacing.lg,
    alignItems: 'center',
  },
  emptyContainer: {
    paddingVertical: Spacing.md,
  },
  emptyText: {
    fontSize: 14,
  },
});
